import classNames from "classnames";
import type * as React from "react";

import { X } from "@/shared/foundation/icons";

import { Spinner } from "../Spinner";

import styles from "./Tag.module.css";
import { tagColor } from "./tagColor";

/**
 * Labels longer than this get the CSS clip and a tooltip with the full value.
 * Above the stylesheet's 20ch cutoff on purpose — `ch` is the width of "0" and
 * tag glyphs are narrower, so text keeps fitting past 20 (measured: overflow
 * starts at 23-25). Gating lower would tooltip labels already fully visible.
 */
export const TAG_MAX_LENGTH = 25;

export interface TagProps {
  label: string;
  /**
   * Optional leading-dot color (any CSS color string).
   */
  color?: string;
  /**
   * Drawn in place of the leading dot, for a tag that names a record (such
   * as an `EntityIcon`) rather than a free-form label.
   */
  icon?: React.ReactNode;
  /**
   * Optional onClick callback. When set, the tag body (dot + label) renders as
   * a real button so it is keyboard- and screen-reader-accessible.
   */
  onClick?: () => void;
  /**
   * Optional onDelete callback. If set, renders a delete button.
   */
  onDelete?: () => void;
  /**
   * Accessible name for the delete button, when "Remove <label>" would
   * misdescribe it. A tag staged for deletion is the case that needs this:
   * its button *cancels* the deletion, so the default name says the opposite
   * of what pressing it does.
   */
  deleteLabel?: string;
  /**
   * For a tag that toggles a filter. `false` dims it, as one of the choices
   * not picked; `true` marks it picked. Left out, the tag is neither.
   */
  selected?: boolean;
  /**
   * `compact` is shorter, for the pills inside a small tag field, which a
   * full-size pill would make taller than the fields beside it. Shown
   * anywhere else, tags keep the full size.
   * @default "md"
   */
  size?: "md" | "compact";
  /**
   * Shows the delete button only while the pointer is on the tag or it has
   * focus, taking no width meanwhile, for a row of tags that are mostly
   * read. Keyboard users reach it as always.
   */
  deleteOnHover?: boolean;
  /**
   * The tag is being saved: dimmed, with a small spinner in place of the
   * delete button.
   */
  busy?: boolean;
}

export function Tag(props: TagProps) {
  const {
    label,
    icon,
    onClick,
    onDelete,
    deleteLabel,
    selected,
    size = "md",
    deleteOnHover = false,
    busy = false,
    color = tagColor(props.label),
  } = props;

  const isTruncated = label.length > TAG_MAX_LENGTH;

  const content = (
    <>
      {icon ?? (
        <span className={styles.dot} style={{ backgroundColor: color }} />
      )}
      <span
        className={isTruncated ? styles.labelTruncated : styles.label}
        title={isTruncated ? label : undefined}
      >
        {label}
      </span>
    </>
  );

  return (
    <span
      className={classNames(styles.tag, {
        [styles.compact]: size === "compact",
        [styles.dimmed]: selected === false,
        [styles.deleteOnHover]: deleteOnHover,
        [styles.busy]: busy,
      })}
      aria-busy={busy || undefined}
    >
      {onClick ? (
        <button
          type="button"
          className={styles.body}
          onClick={onClick}
          aria-pressed={selected}
        >
          {content}
        </button>
      ) : (
        content
      )}
      {busy && <Spinner aria-hidden className={styles.spinner} />}
      {onDelete && !busy && (
        <button
          type="button"
          className={styles.deleteButton}
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          aria-label={deleteLabel ?? `Remove ${label}`}
        >
          <X className={styles.icon} />
        </button>
      )}
    </span>
  );
}
